import { useEffect, useMemo, useRef, useState } from 'react'
import { Bot, Send, X, Maximize2, Minimize2 } from 'lucide-react'
import Button from '@components/ui/Button'
import { useToast } from '@hooks/useToast'
import { getOpenAIClient } from '@services/openai'

export default function FloatingAIChat() {
  const { error, info } = useToast()
  const [open, setOpen] = useState(false)
  const [maximized, setMaximized] = useState(false)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: 'Olá! Posso ajudar com simulações, regras e cenários. O que você precisa?'
  }])
  const endRef = useRef(null)

  const client = useMemo(() => getOpenAIClient(), [])
  const model = useMemo(() => localStorage.getItem('bravo-sim-ai-model') || 'gpt-4o-mini', [])

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [open, messages])

  async function send() {
    const text = input.trim()
    if (!text) return
    if (!client) {
      info('Configure a OpenAI em Configurações para usar o chat.')
      setOpen(true)
      return
    }
    const userMsg = { role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setBusy(true)
    try {
      // SDK browser com Responses API
      const res = await client.responses.create({
        model,
        input: [
          { role: 'system', content: localStorage.getItem('bravo-sim-floating-system-prompt') || 'Você é um assistente do BravoSim.' },
          ...messages,
          userMsg,
        ],
      })
      const out = res?.output_text || res?.content?.[0]?.text || 'Pronto.'
      setMessages((prev) => [...prev, { role: 'assistant', content: out }])
    } catch (e) {
      error('Falha no chat: ' + (e?.message || 'erro desconhecido'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* Botão flutuante */}
      <button
        aria-label="Abrir chat IA"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-primary text-white shadow-large hover:opacity-90 transition h-12 w-12 flex items-center justify-center"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      {open && (
        <div className={maximized
          ? 'fixed inset-0 z-[9999] w-full h-full rounded-none border border-transparent bg-white dark:bg-gray-800 shadow-large flex flex-col overflow-hidden'
          : 'fixed bottom-20 right-6 z-40 w-[360px] max-w-[90vw] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-large flex flex-col overflow-hidden'}>
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">Assistente BravoSim</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setMaximized((v) => !v)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                {maximized ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className={maximized ? 'p-3 flex-1 overflow-y-auto space-y-3 text-sm' : 'p-3 h-72 overflow-y-auto space-y-3 text-sm'}>
            {messages.map((m, idx) => (
              <div key={idx} className={m.role === 'assistant' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}>
                <div className={m.role === 'assistant' ? 'bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2' : 'bg-primary/10 dark:bg-primary/20 rounded-lg px-3 py-2'}>
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <input
              className="flex-1 input input-bordered w-full dark:bg-gray-700 dark:border-gray-600"
              type="text"
              placeholder="Pergunte algo..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
              disabled={busy}
            />
            <Button size="sm" onClick={send} isLoading={busy} disabled={busy || !input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
